import './index.scss';
import React, {FunctionComponent, useCallback, useEffect} from "react";
import LoadingCircles from "../../../components/loading-circles";
import {ParamParseKey, useParams} from "react-router-dom";
import AppRoutes from "../../../../core/models/routes";
import {useIsMounted, useNavigate} from "../../../../@bizkeytech";
import {CodeContentTypeIds, CodeContentTypeIdTypeMap} from "../../../../core/constants/enums";
import ApiService from "../../../../core/services/api";

const RedirectLinkView: FunctionComponent = () => {
    const navigate = useNavigate();
    const params = useParams<ParamParseKey<typeof AppRoutes.public.redirectLink>>();
    const isMounted = useIsMounted();

    /**
     * Fetches the redirection link from the token from the server.
     *
     * - If the response is successful, it redirects the user to the redirection link.
     * - If the response is unsuccessful, it redirects the user to the access denied page.
     * - If the response is empty, it redirects the user to the not found page.
     */
    const getRedirectionLinkFromToken = useCallback(async (abortController: AbortController) => {
        const response = await ApiService.scanCode(params.token!, abortController);
        if (!isMounted() || response?.aborted)
            return;
        if (!response || !response.resultFlag)
            return navigate(AppRoutes.public.error.notFound, {replace: true});

        if (response.data) {
            switch (response.data.contentTypeId) {
                case CodeContentTypeIds.externalLink: {
                    if (!(response.data.link.startsWith('http://') || response.data.link.startsWith('https://'))) {
                        response.data.link = `https://${response.data.link}`;
                    }
                    window.location.href = response.data.link;
                    break;
                }
                default: {
                    navigate(AppRoutes.generateRoute(AppRoutes.public.viewContent, {
                        contentType: CodeContentTypeIdTypeMap[response.data.contentTypeId],
                        contentId: response.data.link,
                    }), {replace: true});
                    break;
                }
            }
        } else {
            navigate(AppRoutes.public.error.notFound, {replace: true});
        }
    }, [isMounted, navigate, params.token])

    /**
     * As soon as the component mounts:
     * - Fetches the redirection link from the token when the component mounts.
     */
    useEffect(() => {
        // // TODO: remove this line and uncomment the below code after implementing the API.
        // setTimeout(() => {
        //     if (params.token === '1') {
        //         return navigate(AppRoutes.generateRoute(AppRoutes.public.viewContent, {
        //             contentType: CodeContentTypes.virtualCard,
        //             contentId: '123213-123124123-1413421-312413',
        //         }), {replace: true});
        //     } else if (params.token === '2') {
        //         return navigate(AppRoutes.generateRoute(AppRoutes.public.viewContent, {
        //             contentType: CodeContentTypes.pdf,
        //             contentId: '123213-123124123-1413421-312413',
        //         }), {replace: true});
        //     } else if (params.token === '3') {
        //         return navigate(AppRoutes.generateRoute(AppRoutes.public.viewContent, {
        //             contentType: CodeContentTypes.imageGallery,
        //             contentId: '123213-123124123-1413421-312413',
        //         }), {replace: true});
        //     } else if (params.token === '4') {
        //         return navigate(AppRoutes.generateRoute(AppRoutes.public.viewContent, {
        //             contentType: CodeContentTypes.socialMedia,
        //             contentId: '123213-123124123-1413421-312413',
        //         }), {replace: true});
        //     } else if (params.token === '5') {
        //         return navigate(AppRoutes.generateRoute(AppRoutes.public.viewContent, {
        //             contentType: CodeContentTypes.app,
        //             contentId: '123213-123124123-1413421-312413',
        //         }), {replace: true});
        //     } else if (params.token === '6') {
        //         return navigate(AppRoutes.generateRoute(AppRoutes.public.viewContent, {
        //             contentType: CodeContentTypes.video,
        //             contentId: '123213-123124123-1413421-312413',
        //         }), {replace: true});
        //     } else if (params.token === '7') {
        //         return navigate(AppRoutes.generateRoute(AppRoutes.public.viewContent, {
        //             contentType: CodeContentTypes.feedback,
        //             contentId: '123213-123124123-1413421-312413',
        //         }), {replace: true});
        //     }
        // }, 2000)
        const abortController = new AbortController();
        getRedirectionLinkFromToken(abortController).then();
        return () => {
            abortController.abort();
        }
    }, [getRedirectionLinkFromToken, navigate, params.token]);

    return (
        <>
            <div className={'redirect-link-view'}>
                <LoadingCircles text={'Redirecting You'}/>
            </div>
        </>
    );
}

export default RedirectLinkView;
